import React, { useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    ModalFooter,
    Box,
    Flex,
    VStack,
    HStack,
    Icon,
    Text,
    Input,
    InputGroup,
    InputLeftElement,
    Button,
    Select,
    Avatar,
    Divider,
    Spinner,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Checkbox,
    CheckboxGroup,
    useToast,
    useColorModeValue,
} from "@chakra-ui/react";
import { Tooltip } from "./Tip";
import { SearchIcon } from '@chakra-ui/icons';
import { FiUserPlus, FiTrash2, FiShare2, FiUsers, FiInfo } from 'react-icons/fi';
import { alpha, calWords, useCalTheme, CalStatusPill, CalError } from './ui';
import {
  GET_CALENDAR_SHARES,
  CALENDAR_SEARCH_PEOPLE,
  SHARE_CALENDAR_WITH_CLIENTS,
  UNSHARE_CALENDAR,
  GET_CALENDAR_AUDIENCE_OPTIONS,
  GET_CALENDAR_AUDIENCE_SHARES,
  SHARE_CALENDAR_WITH_AUDIENCES,
  PREVIEW_CALENDAR_AUDIENCES,
  UNSHARE_CALENDAR_AUDIENCE,
} from '../queries';

/**
 * ShareCalendarModal — share a calendar with people or whole groups.
 *
 * People tab: type a first name, last name or nickname to find someone
 * (employees show with their roles; terminated staff never appear), choose
 * per person: Can view / Can view & edit.
 *
 * Groups tab: share with everyone holding an employee ROLE (active staff,
 * live rule — new hires with the role gain access automatically) or a client
 * TAG. A preview of exactly who that reaches RIGHT NOW always shows before
 * the share button unlocks.
 *
 * Everyone reached gets an in-app notification (backend-driven).
 * Baby-boomer standard: plain-English tooltips on every control.
 */

interface PendingShare {
  clientId: string;
  name: string;
  email?: string;
  permission: 'VIEW' | 'EDIT';
  isEmployee: boolean;
  roleNames?: string[];
}

interface ShareCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  calendarId: string;
  calendarName: string;
  /** Called after a successful share/unshare so parents can refetch */
  onChanged?: () => void;
}

/** "Katherine (Katie) Mansfield" — nickname in brackets when it differs from the first name. */
export const formatPersonName = (p: {
  fName?: string | null;
  lName?: string | null;
  preferredName?: string | null;
  email?: string | null;
}): string => {
  const first = (p.fName || '').trim();
  const last = (p.lName || '').trim();
  const nick = (p.preferredName || '').trim();
  const showNick = nick && nick.toLowerCase() !== first.toLowerCase();
  const name = `${first}${showNick ? ` (${nick})` : ''} ${last}`.trim();
  return name || p.email || 'Unnamed';
};

/** Staff member / client, plus up to two roles — plain-words pills, never tiny capitals. */
const PersonBadges: React.FC<{ isEmployee: boolean; roleNames?: string[] | null }> = ({
  isEmployee,
  roleNames,
}) => (
  <Flex gap={1.5} flexWrap="wrap">
    {isEmployee ? (
      <Tooltip label="This person has an employee record on this site">
        <Box as="span" display="inline-flex" cursor="help"><CalStatusPill status="EMPLOYEE" label="Staff member" tone="accent" /></Box>
      </Tooltip>
    ) : (
      <Tooltip label="This person is a client record (no employee record)">
        <Box as="span" display="inline-flex" cursor="help"><CalStatusPill status="CLIENT" label="Client" tone="neutral" /></Box>
      </Tooltip>
    )}
    {(roleNames || []).slice(0, 2).map((r) => (
      <CalStatusPill key={r} status="ROLE" label={r} tone="purple" />
    ))}
    {(roleNames?.length || 0) > 2 && (
      <Tooltip label={roleNames!.slice(2).join(', ')}>
        <Box as="span" display="inline-flex" cursor="help"><CalStatusPill status="MORE" label={`+${roleNames!.length - 2} more`} tone="purple" /></Box>
      </Tooltip>
    )}
  </Flex>
);

const ShareCalendarModal: React.FC<ShareCalendarModalProps> = ({
  isOpen,
  onClose,
  calendarId,
  calendarName,
  onChanged,
}) => {
  const toast = useToast();
  const t = useCalTheme();
  // A modal needs a solid surface in both modes (the kit's card surface is glass).
  const modalBg = useColorModeValue('white', 'gray.900');

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pending, setPending] = useState<PendingShare[]>([]);

  // Group-share state — several roles/tags can be picked at once
  const [groupKind, setGroupKind] = useState<'ROLE' | 'TAG'>('ROLE');
  const [groupValues, setGroupValues] = useState<string[]>([]);
  const [groupPermission, setGroupPermission] = useState<'VIEW' | 'EDIT'>('VIEW');

  // Debounce the typeahead so we don't query on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const {
    data: sharesData,
    loading: sharesLoading,
    error: sharesError,
    refetch: refetchShares,
  } = useQuery(GET_CALENDAR_SHARES, {
    variables: { calendarId },
    skip: !isOpen || !calendarId,
    fetchPolicy: 'cache-and-network',
  });

  const { data: audienceSharesData, refetch: refetchAudienceShares } = useQuery(
    GET_CALENDAR_AUDIENCE_SHARES,
    {
      variables: { calendarId },
      skip: !isOpen || !calendarId,
      fetchPolicy: 'cache-and-network',
    }
  );

  const { data: optionsData } = useQuery(GET_CALENDAR_AUDIENCE_OPTIONS, {
    skip: !isOpen,
  });

  const [runSearch, { data: searchData, loading: searchLoading }] = useLazyQuery(
    CALENDAR_SEARCH_PEOPLE,
    { fetchPolicy: 'network-only' }
  );

  useEffect(() => {
    if (debouncedSearch.length >= 2) {
      runSearch({ variables: { search: debouncedSearch } });
    }
  }, [debouncedSearch, runSearch]);

  // Mandatory preview: fires whenever the group selection changes (union, deduped)
  const [runPreview, { data: previewData, loading: previewLoading }] = useLazyQuery(
    PREVIEW_CALENDAR_AUDIENCES,
    { fetchPolicy: 'network-only' }
  );

  useEffect(() => {
    if (groupValues.length) {
      runPreview({
        variables: { targets: groupValues.map((value) => ({ kind: groupKind, value })) },
      });
    }
  }, [groupKind, groupValues, runPreview]);

  const refetchAll = () => {
    refetchShares();
    refetchAudienceShares();
    onChanged?.();
  };

  const [shareCalendarWithClients, { loading: sharing }] = useMutation(SHARE_CALENDAR_WITH_CLIENTS, {
    onCompleted: () => {
      toast({
        title: 'Calendar shared',
        description: 'Each person now has access and has been sent a notification.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      setPending([]);
      refetchAll();
    },
    onError: (error) => {
      toast({ title: 'Could not share the calendar', description: error.message, status: 'error', duration: 6000, isClosable: true });
    },
  });

  const [unshareCalendar, { loading: unsharing }] = useMutation(UNSHARE_CALENDAR, {
    onCompleted: () => {
      toast({ title: 'Access removed', status: 'success', duration: 3000, isClosable: true });
      refetchAll();
    },
    onError: (error) => {
      toast({ title: 'Could not remove access', description: error.message, status: 'error', duration: 6000, isClosable: true });
    },
  });

  const [shareWithAudiences, { loading: audienceSharing }] = useMutation(SHARE_CALENDAR_WITH_AUDIENCES, {
    onCompleted: () => {
      toast({
        title: 'Group share added',
        description: 'Everyone in the groups has been notified (once each). People who join a group later gain access automatically.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setGroupValues([]);
      refetchAll();
    },
    onError: (error) => {
      toast({ title: 'Could not add the group share', description: error.message, status: 'error', duration: 6000, isClosable: true });
    },
  });

  const [unshareAudience, { loading: audienceUnsharing }] = useMutation(UNSHARE_CALENDAR_AUDIENCE, {
    onCompleted: () => {
      toast({ title: 'Group share removed', description: 'Everyone in the group has been notified.', status: 'success', duration: 4000, isClosable: true });
      refetchAll();
    },
    onError: (error) => {
      toast({ title: 'Could not remove the group share', description: error.message, status: 'error', duration: 6000, isClosable: true });
    },
  });

  // useMemo so the searchResults memo below doesn't see a fresh [] each render
  const existingShares: any[] = useMemo(() => sharesData?.calendarShares || [], [sharesData]);
  const audienceRules: any[] = audienceSharesData?.calendarAudienceShares || [];
  const previewMembers: any[] = previewData?.previewCalendarAudiences || [];
  const roleOptions: any[] = optionsData?.calendarAudienceOptions?.roles || [];
  const tagOptions: string[] = optionsData?.calendarAudienceOptions?.tags || [];

  // Hide people already shared-with (or already queued) from the search results
  const searchResults = useMemo(() => {
    const taken = new Set([
      ...existingShares.map((s) => s.clientId),
      ...pending.map((p) => p.clientId),
    ]);
    return (searchData?.calendarSearchPeople || []).filter((c: any) => !taken.has(c.clientId));
  }, [searchData, existingShares, pending]);

  const addPending = (person: any) => {
    setPending((prev) => [
      ...prev,
      {
        clientId: person.clientId,
        name: formatPersonName(person),
        email: person.email || undefined,
        permission: 'VIEW',
        isEmployee: person.isEmployee,
        roleNames: person.roleNames || undefined,
      },
    ]);
    setSearchTerm('');
    setDebouncedSearch('');
  };

  const setPendingPermission = (clientId: string, permission: 'VIEW' | 'EDIT') => {
    setPending((prev) => prev.map((p) => (p.clientId === clientId ? { ...p, permission } : p)));
  };

  const removePending = (clientId: string) => {
    setPending((prev) => prev.filter((p) => p.clientId !== clientId));
  };

  const handleShare = () => {
    if (!pending.length) return;
    shareCalendarWithClients({
      variables: {
        calendarId,
        shares: pending.map((p) => ({ clientId: p.clientId, permission: p.permission })),
      },
    });
  };

  const handleUpdateExisting = (clientId: string, permission: 'VIEW' | 'EDIT') => {
    shareCalendarWithClients({
      variables: { calendarId, shares: [{ clientId, permission }] },
    });
  };

  const handleGroupShare = () => {
    if (!groupValues.length) return;
    shareWithAudiences({
      variables: {
        calendarId,
        targets: groupValues.map((value) => ({ kind: groupKind, value })),
        permission: groupPermission,
      },
    });
  };

  // Words come from the module's one map (CAL_WORDS.share): "Can view" / "Can edit". ADMIN reads as edit.
  const permissionPill = (permission: string) => (
    <CalStatusPill kind="share" status={permission === 'EDIT' || permission === 'ADMIN' ? 'EDIT' : 'VIEW'} />
  );
  const permissionOptions = (
    <>
      <option value="VIEW">{calWords('share', 'VIEW')}</option>
      <option value="EDIT">{calWords('share', 'EDIT')}</option>
    </>
  );
  const permissionTip = '"Can view" lets them see events. "Can edit" also lets them add and change events.';

  /** A red worded remove button — ≥44px, words not a bare bin icon. */
  const removeButton = (label: string, tip: string, onClick: () => void, isLoading?: boolean) => (
    <Tooltip shouldWrapChildren label={tip}>
      <Button
        leftIcon={<FiTrash2 />}
        size="md"
        minH="44px"
        variant="ghost"
        color={t.danger}
        _hover={{ bg: alpha(t.danger, 0.1) }}
        isLoading={isLoading}
        onClick={onClick}
        aria-label={label}
      >
        Remove
      </Button>
    </Tooltip>
  );

  const rowProps = {
    p: 3,
    minH: '48px',
    bg: t.raised,
    borderWidth: '1px',
    borderColor: t.border,
    borderRadius: 'lg',
    gap: 3,
    flexWrap: 'wrap' as const,
    align: 'center',
  };

  const groupValueLabels = groupValues
    .map((v) => (groupKind === 'ROLE' ? roleOptions.find((r) => r.id === v)?.name || v : v))
    .join(', ');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'xl' }} scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(6px)" />
      <ModalContent
        bg={modalBg}
        borderWidth="1px"
        borderColor={t.border}
        margin={{ base: 0, md: 'auto' }}
        borderRadius={{ base: 0, md: '2xl' }}
      >
        <ModalHeader pr={14}>
          <HStack spacing={3} align="center">
            <Flex flexShrink={0} w={10} h={10} borderRadius="lg" bgImage={t.accentGradient} align="center" justify="center">
              <Icon as={FiShare2} color="white" />
            </Flex>
            <Text fontSize={{ base: 'lg', md: 'xl' }} color={t.text} wordBreak="break-word" letterSpacing="-0.01em">
              Share “{calendarName}”
            </Text>
          </HStack>
          <Text fontSize="md" fontWeight="normal" color={t.muted} mt={2}>
            Give people — or whole groups — access to see or help manage this calendar.
          </Text>
        </ModalHeader>
        <Tooltip label="Close this window without making more changes">
          <ModalCloseButton size="lg" w="44px" h="44px" top={3} right={3} color={t.text} />
        </Tooltip>

        <ModalBody pb={4}>
          <VStack spacing={5} align="stretch">
            <Tabs variant="soft-rounded" colorScheme="blue">
              <TabList gap={2} flexWrap="wrap">
                <Tooltip label="Share with one person at a time — search them by name">
                  <Tab minH="44px" fontSize="md" color={t.muted} _selected={{ bg: alpha(t.primary, 0.16), color: t.text }}>
                    <HStack spacing={2}><Icon as={FiUserPlus} /><Text>A person</Text></HStack>
                  </Tab>
                </Tooltip>
                <Tooltip label="Share with everyone holding a role (e.g. all active Registered Nurses) or a client tag — updates itself as people join or leave the group">
                  <Tab minH="44px" fontSize="md" color={t.muted} _selected={{ bg: alpha(t.primary, 0.16), color: t.text }}>
                    <HStack spacing={2}><Icon as={FiUsers} /><Text>A group</Text></HStack>
                  </Tab>
                </Tooltip>
              </TabList>

              <TabPanels>
                {/* ============ PEOPLE TAB ============ */}
                <TabPanel px={0}>
                  <VStack spacing={4} align="stretch">
                    <Tooltip label="Start typing a first name, last name or nickname — matching people appear below. People who no longer work here won't show up.">
                      {/* A full-width Box: the tooltip's own span would shrink the search box to half the row. */}
                      <Box w="100%">
                        <InputGroup size="lg">
                          <InputLeftElement pointerEvents="none">
                            <SearchIcon color={t.faint} />
                          </InputLeftElement>
                          <Input
                            placeholder="Type a name or nickname to search…"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            focusBorderColor={t.primary}
                          />
                        </InputGroup>
                      </Box>
                    </Tooltip>
                    {searchTerm.trim().length === 1 && (
                      <Text fontSize="sm" color={t.muted}>Type at least two letters to search.</Text>
                    )}

                    {searchLoading && debouncedSearch.length >= 2 && (
                      <HStack role="status">
                        <Spinner size="sm" color={t.primary} />
                        <Text fontSize="md" color={t.muted}>Searching…</Text>
                      </HStack>
                    )}

                    {debouncedSearch.length >= 2 && !searchLoading && (
                      <VStack
                        align="stretch"
                        spacing={0}
                        maxH="260px"
                        overflowY="auto"
                        borderWidth="1px"
                        borderColor={t.border}
                        borderRadius="lg"
                      >
                        {searchResults.length === 0 && (
                          <Text p={3} fontSize="md" color={t.muted}>
                            No one found matching “{debouncedSearch}”. Check the spelling, or they may
                            already have access below.
                          </Text>
                        )}
                        {searchResults.map((person: any) => (
                          <Tooltip
                            key={person.clientId}
                            label={`Add ${formatPersonName(person)} to the share list — nothing is shared until you press Share`}
                          >
                            <Flex
                              as="button"
                              type="button"
                              textAlign="left"
                              p={3}
                              minH="56px"
                              gap={3}
                              align="center"
                              flexWrap="wrap"
                              cursor="pointer"
                              borderBottomWidth="1px"
                              borderColor={t.border}
                              _hover={{ bg: t.surfaceHover }}
                              onClick={() => addPending(person)}
                            >
                              <Avatar size="sm" name={formatPersonName(person)} />
                              <Box flex="1" minW="140px">
                                <Text color={t.text} fontSize="md" fontWeight="medium" wordBreak="break-word">
                                  {formatPersonName(person)}
                                </Text>
                                {person.email && (
                                  <Text color={t.muted} fontSize="sm" wordBreak="break-all">
                                    {person.email}
                                  </Text>
                                )}
                                <Box mt={1}><PersonBadges isEmployee={person.isEmployee} roleNames={person.roleNames} /></Box>
                              </Box>
                              <HStack spacing={1.5} color={t.primary} fontWeight="semibold" fontSize="md" flexShrink={0}>
                                <Icon as={FiUserPlus} />
                                <Text as="span">Add</Text>
                              </HStack>
                            </Flex>
                          </Tooltip>
                        ))}
                      </VStack>
                    )}

                    {/* Pending (about to be shared) */}
                    {pending.length > 0 && (
                      <Box>
                        <Text color={t.text} fontSize="md" fontWeight="semibold" mb={2}>
                          Ready to share with
                        </Text>
                        <VStack align="stretch" spacing={2}>
                          {pending.map((p) => (
                            <Flex key={p.clientId} {...rowProps}>
                              <Avatar size="sm" name={p.name} />
                              <Box flex="1" minW="140px">
                                <Text color={t.text} fontSize="md" fontWeight="medium" wordBreak="break-word">
                                  {p.name}
                                </Text>
                                <Box mt={1}><PersonBadges isEmployee={p.isEmployee} roleNames={p.roleNames} /></Box>
                              </Box>
                              <Flex gap={2} flexWrap="wrap" w={{ base: '100%', sm: 'auto' }}>
                                <Tooltip label={permissionTip}>
                                  <Box flex={{ base: 1, sm: 'none' }} w={{ sm: '170px' }}>
                                    <Select
                                      value={p.permission}
                                      onChange={(e) => setPendingPermission(p.clientId, e.target.value as 'VIEW' | 'EDIT')}
                                      size="lg"
                                      focusBorderColor={t.primary}
                                      aria-label={`What ${p.name} can do`}
                                    >
                                      {permissionOptions}
                                    </Select>
                                  </Box>
                                </Tooltip>
                                {removeButton(`Remove ${p.name} from the share list`, `Remove ${p.name} from this list before sharing — nothing is saved`, () => removePending(p.clientId))}
                              </Flex>
                            </Flex>
                          ))}
                        </VStack>
                        <Tooltip label={`Give ${pending.length} ${pending.length === 1 ? 'person' : 'people'} access — each will get a notification`}>
                          <Box mt={3}>
                            <Button
                              w={{ base: '100%', sm: 'auto' }}
                              leftIcon={<FiShare2 />}
                              bgImage={t.accentGradient}
                              color="white"
                              _hover={{ opacity: 0.92 }}
                              size="lg"
                              minH="48px"
                              onClick={handleShare}
                              isLoading={sharing}
                              loadingText="Sharing…"
                            >
                              Share with {pending.length} {pending.length === 1 ? 'person' : 'people'}
                            </Button>
                          </Box>
                        </Tooltip>
                      </Box>
                    )}
                  </VStack>
                </TabPanel>

                {/* ============ GROUPS TAB ============ */}
                <TabPanel px={0}>
                  <VStack spacing={4} align="stretch">
                    <HStack align="start" spacing={3} p={3} borderRadius="lg" bg={alpha(t.primary, 0.08)} borderWidth="1px" borderColor={alpha(t.primary, 0.25)}>
                      <Icon as={FiInfo} color={t.primary} mt="3px" flexShrink={0} />
                      <Text fontSize="md" color={t.text}>
                        A group share stays up to date by itself: anyone who joins the role or tag later
                        gets access automatically, and anyone who leaves loses it.
                      </Text>
                    </HStack>

                    <Flex gap={3} flexWrap="wrap" direction={{ base: 'column', md: 'row' }}>
                      <Tooltip label="Choose whether to share by employee role or by client tag">
                        <Box flex={1} minW={0}>
                          <Text fontSize="sm" fontWeight="semibold" color={t.muted} mb={1}>Share with</Text>
                          <Select
                            value={groupKind}
                            onChange={(e) => { setGroupKind(e.target.value as 'ROLE' | 'TAG'); setGroupValues([]); }}
                            size="lg"
                            focusBorderColor={t.primary}
                          >
                            <option value="ROLE">Employees with a role</option>
                            <option value="TAG">Clients with a tag</option>
                          </Select>
                        </Box>
                      </Tooltip>

                      <Tooltip label={'"Can view" lets the groups see events. "Can edit" also lets them add and change events.'}>
                        <Box flex={1} minW={0}>
                          <Text fontSize="sm" fontWeight="semibold" color={t.muted} mb={1}>They can</Text>
                          <Select
                            value={groupPermission}
                            onChange={(e) => setGroupPermission(e.target.value as 'VIEW' | 'EDIT')}
                            size="lg"
                            focusBorderColor={t.primary}
                          >
                            {permissionOptions}
                          </Select>
                        </Box>
                      </Tooltip>
                    </Flex>

                    {/* Multi-select: tick as many roles/tags as you like */}
                    <Box borderWidth="1px" borderColor={t.border} borderRadius="lg" p={3}>
                      <Text color={t.text} fontSize="md" fontWeight="semibold" mb={2}>
                        {groupKind === 'ROLE'
                          ? 'Tick one or more roles — only active employees holding them are included'
                          : 'Tick one or more tags — every client carrying them is included'}
                      </Text>
                      <CheckboxGroup
                        value={groupValues}
                        onChange={(vals) => setGroupValues(vals as string[])}
                      >
                        <VStack align="stretch" spacing={0} maxH="220px" overflowY="auto">
                          {(groupKind === 'ROLE' ? roleOptions.map((r) => ({ v: r.id, label: r.name })) : tagOptions.map((tag) => ({ v: tag, label: tag }))).map(({ v, label }) => (
                            <Tooltip key={v} label={`Include everyone ${groupKind === 'ROLE' ? `with the ${label} role` : `tagged ${label}`} in this share`}>
                              <Box minH="44px" display="flex" alignItems="center">
                                <Checkbox value={v} size="lg" colorScheme="green">
                                  <Text fontSize="md" color={t.text}>{label}</Text>
                                </Checkbox>
                              </Box>
                            </Tooltip>
                          ))}
                          {(groupKind === 'ROLE' ? roleOptions : tagOptions).length === 0 && (
                            <Text fontSize="md" color={t.muted}>
                              {groupKind === 'ROLE' ? 'No roles are set up on this site yet.' : 'No client tags exist on this site yet.'}
                            </Text>
                          )}
                        </VStack>
                      </CheckboxGroup>
                    </Box>

                    {/* Mandatory preview */}
                    {groupValues.length > 0 && (
                      <Box borderWidth="1px" borderColor={t.border} borderRadius="lg" p={3}>
                        {previewLoading ? (
                          <HStack role="status">
                            <Spinner size="sm" color={t.primary} />
                            <Text fontSize="md" color={t.muted}>Checking who this reaches…</Text>
                          </HStack>
                        ) : (
                          <>
                            <Text color={t.text} fontSize="md" fontWeight="semibold" mb={2}>
                              This will share with {previewMembers.length}{' '}
                              {previewMembers.length === 1 ? 'person' : 'people'} right now:
                            </Text>
                            {previewMembers.length === 0 && (
                              <Text fontSize="md" color={t.muted}>
                                No one currently matches — the share would only take effect when someone
                                joins this {groupKind === 'ROLE' ? 'role' : 'tag'}.
                              </Text>
                            )}
                            <VStack align="stretch" spacing={1} maxH="220px" overflowY="auto">
                              {previewMembers.map((m: any) => (
                                <Flex key={m.clientId} p={2} gap={3} align="center" flexWrap="wrap">
                                  <Avatar size="sm" name={formatPersonName(m)} />
                                  <Text color={t.text} fontSize="md" flex="1" minW="120px" wordBreak="break-word">
                                    {formatPersonName(m)}
                                  </Text>
                                  <PersonBadges isEmployee={m.isEmployee} roleNames={m.roleNames} />
                                </Flex>
                              ))}
                            </VStack>
                            <Tooltip shouldWrapChildren
                              label={`Share this calendar with ${groupKind === 'ROLE' ? `everyone holding: ${groupValueLabels}` : `everyone tagged: ${groupValueLabels}`} — the ${previewMembers.length} ${previewMembers.length === 1 ? 'person' : 'people'} above will each be notified once`}
                            >
                              <Button
                                mt={3}
                                leftIcon={<FiUsers />}
                                bg={t.success}
                                color="white"
                                _hover={{ opacity: 0.9 }}
                                size="lg"
                                minH="48px"
                                onClick={handleGroupShare}
                                isLoading={audienceSharing}
                                loadingText="Sharing…"
                                isDisabled={previewMembers.length === 0 && groupKind === 'TAG'}
                              >
                                Share with {groupValues.length === 1 ? 'this group' : `these ${groupValues.length} groups`}
                              </Button>
                            </Tooltip>
                            {previewMembers.length === 0 && groupKind === 'TAG' && (
                              <Text fontSize="sm" color={t.muted} mt={2}>
                                Sharing is off until at least one client carries the tag.
                              </Text>
                            )}
                          </>
                        )}
                      </Box>
                    )}
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>

            <Divider borderColor={t.border} />

            {/* Existing access — loading / error / empty are three different truths */}
            <Box>
              <Text color={t.text} fontSize="md" fontWeight="semibold" mb={2}>
                Who already has access
              </Text>
              {sharesError && !existingShares.length && !audienceRules.length ? (
                <CalError title="Couldn't load who has access" error={sharesError} onRetry={() => refetchShares()} />
              ) : sharesLoading && !existingShares.length && !audienceRules.length ? (
                <HStack role="status">
                  <Spinner size="sm" color={t.primary} />
                  <Text fontSize="md" color={t.muted}>Loading who has access…</Text>
                </HStack>
              ) : existingShares.length === 0 && audienceRules.length === 0 ? (
                <HStack align="start" spacing={3} p={3} borderRadius="lg" bg={t.raised} borderWidth="1px" borderColor={t.border}>
                  <Icon as={FiInfo} color={t.faint} mt="3px" flexShrink={0} />
                  <Text fontSize="md" color={t.muted}>
                    This calendar hasn’t been shared with anyone yet. Only you (and site admins) can see it.
                  </Text>
                </HStack>
              ) : (
                <VStack align="stretch" spacing={2}>
                  {/* Group rules first */}
                  {audienceRules.map((rule) => (
                    <Flex key={`${rule.kind}:${rule.value}`} {...rowProps} borderColor={alpha(t.primary, 0.45)}>
                      <Flex flexShrink={0} w={8} h={8} borderRadius="lg" bg={alpha(t.primary, 0.14)} align="center" justify="center">
                        <Icon as={FiUsers} color={t.primary} />
                      </Flex>
                      <Box flex="1" minW="160px">
                        <Text color={t.text} fontSize="md" fontWeight="medium" wordBreak="break-word">
                          {rule.kind === 'ROLE' ? `Everyone with the role “${rule.label || rule.value}”` : `Everyone tagged “${rule.label || rule.value}”`}
                        </Text>
                        <Flex gap={2} mt={1} flexWrap="wrap" align="center">
                          {permissionPill(rule.permission)}
                          <Tooltip label="How many people this group rule reaches right now — it updates automatically as people join or leave">
                            <Box as="span" display="inline-flex" cursor="help">
                              <CalStatusPill status="MEMBERS" label={`${rule.memberCount} ${rule.memberCount === 1 ? 'person' : 'people'} right now`} tone="success" />
                            </Box>
                          </Tooltip>
                          {rule.sharedByName && (
                            <Text color={t.muted} fontSize="sm">
                              Shared by {rule.sharedByName}
                            </Text>
                          )}
                        </Flex>
                      </Box>
                      {removeButton(
                        `Remove the ${rule.label || rule.value} group share`,
                        'Stop sharing with this group — everyone currently in it will be notified',
                        () => unshareAudience({ variables: { calendarId, kind: rule.kind, value: rule.value } }),
                        audienceUnsharing,
                      )}
                    </Flex>
                  ))}

                  {/* Individual shares */}
                  {existingShares.map((s) => {
                    const personName = formatPersonName({ fName: s.clientName, lName: '', preferredName: s.preferredName, email: s.clientEmail });
                    return (
                      <Flex key={s.clientId} {...rowProps}>
                        <Avatar size="sm" name={s.clientName || 'Unknown'} />
                        <Box flex="1" minW="160px">
                          <Text color={t.text} fontSize="md" fontWeight="medium" wordBreak="break-word">
                            {personName}
                          </Text>
                          <Flex gap={2} mt={1} flexWrap="wrap" align="center">
                            {permissionPill(s.permission)}
                            <PersonBadges isEmployee={s.isEmployee} roleNames={s.roleNames} />
                            {s.sharedByName && (
                              <Text color={t.muted} fontSize="sm">
                                Shared by {s.sharedByName}
                              </Text>
                            )}
                          </Flex>
                        </Box>
                        <Flex gap={2} flexWrap="wrap" w={{ base: '100%', sm: 'auto' }}>
                          <Tooltip label="Change what this person can do on the calendar — saved straight away, and they're notified">
                            <Box flex={{ base: 1, sm: 'none' }} w={{ sm: '170px' }}>
                              <Select
                                value={s.permission === 'EDIT' || s.permission === 'ADMIN' ? 'EDIT' : 'VIEW'}
                                onChange={(e) => handleUpdateExisting(s.clientId, e.target.value as 'VIEW' | 'EDIT')}
                                size="lg"
                                focusBorderColor={t.primary}
                                isDisabled={sharing}
                                aria-label={`What ${personName} can do`}
                              >
                                {permissionOptions}
                              </Select>
                            </Box>
                          </Tooltip>
                          {removeButton(
                            `Remove access for ${s.clientName || 'this person'}`,
                            `Stop sharing this calendar with ${s.clientName || 'this person'} — they'll be notified`,
                            () => unshareCalendar({ variables: { calendarId, clientId: s.clientId } }),
                            unsharing,
                          )}
                        </Flex>
                      </Flex>
                    );
                  })}
                </VStack>
              )}
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Tooltip label="Close this window — any shares you've already made are saved">
            <Button variant="outline" size="lg" minH="48px" w={{ base: '100%', sm: 'auto' }} onClick={onClose}>
              Done
            </Button>
          </Tooltip>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ShareCalendarModal;
